import { DESCRIPTION_MAX, NAME_MAX, type PackageFieldErrors, type PackageForm } from './packageUtils';
import type { PackagesT } from './packagesI18n';

type TextKey = 'nameEn' | 'nameAr' | 'descriptionEn' | 'descriptionAr';

export interface PackageTextFieldsProps {
  t: PackagesT;
  values: Pick<PackageForm, TextKey>;
  disabled?: boolean;
  fieldErrors: PackageFieldErrors;
  onChange: (key: TextKey, value: string) => void;
}

const FIELDS: { key: TextKey; max: number; multiline: boolean; ar: boolean; optional: boolean }[] = [
  { key: 'nameEn', max: NAME_MAX, multiline: false, ar: false, optional: false },
  { key: 'nameAr', max: NAME_MAX, multiline: false, ar: true, optional: true },
  { key: 'descriptionEn', max: DESCRIPTION_MAX, multiline: true, ar: false, optional: true },
  { key: 'descriptionAr', max: DESCRIPTION_MAX, multiline: true, ar: true, optional: true },
];

/** Spec 081b — EN/AR name (≤ 60) and description (≤ 1000) with counters; Arabic inputs are RTL. */
export default function PackageTextFields({ t, values, disabled, fieldErrors, onChange }: PackageTextFieldsProps) {
  return (
    <>
      <div className="offer-grid-2">
        {FIELDS.map(({ key, max, multiline, ar, optional }) => {
          const id = `pkg-${key}`;
          const error = fieldErrors[key];
          const common = {
            id,
            dir: ar ? 'rtl' : 'ltr',
            lang: ar ? 'ar' : undefined,
            maxLength: max,
            value: values[key],
            disabled,
            ...(error ? { 'aria-invalid': true as const, 'aria-describedby': `pkg-err-${key}` } : {}),
          };
          return (
            <div className="offer-field" key={key}>
              <label htmlFor={id}>
                {t(`packages.field.${key}`)}
                {optional && <span className="offer-optional"> ({t('packages.field.optional')})</span>}
              </label>
              {multiline
                ? <textarea {...common} rows={4} className="pkg-textarea" onChange={(e) => onChange(key, e.target.value)} />
                : <input {...common} type="text" onChange={(e) => onChange(key, e.target.value)} />}
              <div className="offer-counter" dir="ltr">{values[key].length}/{max}</div>
              {error && <div className="offer-field-error" id={`pkg-err-${key}`} role="alert">{error}</div>}
            </div>
          );
        })}
      </div>
      <p className="offer-field-hint">{t('packages.hint.arabic')}</p>
    </>
  );
}
